import { useState } from "react";

import { Box, Button, Grid, IconButton } from "@mui/material";
import docIconFileJpg from "assets/images/docIcon.jpg";
import fileIconUrl from "assets/images/fileIcon.png";
import FormControlLabel from "components/FormControlLabel";
import { base, gray, primary } from "configs/colors";
import { shadows } from "configs/shadows";
import { useFileStorageControllerUploadFiles } from "generated";
import { FileUploader } from "react-drag-drop-files";
import {
  Controller,
  FieldPath,
  FieldValues,
  useFormContext,
} from "react-hook-form";

import { EyeIcon, Trash03Icon, UploadCloud02Icon } from "../Icon";
import Typography from "../Typography";

import "./style.scss";

interface Props {
  onChange: (...event: any[]) => void;
  value:
    | {
        newFileName: string;
        originalName: string;
        url: string;
        size: string;
        extname: string;
      }[]
    | undefined;
  disable: boolean | undefined;
  fileType: any;
  maxFile?: number;
  isViewForm: boolean;
}

export const handleShowIcon = (extendName: string, url: string) => {
  if (extendName === ".pdf") {
    return fileIconUrl;
  }
  if (extendName === ".docx" || extendName === ".doc") {
    return docIconFileJpg;
  }
  return url;
};

const Control = (props: Props) => {
  const { onChange, value, disable, fileType, maxFile, isViewForm } = props;
  const [isImage, setIsImage] = useState<boolean>(true);
  const { mutate } = useFileStorageControllerUploadFiles({
    mutation: {
      onSuccess(data: any) {
        const dataResult: any = data?.data;
        if (value && value.length > 0) {
          let input = [...value, ...dataResult];
          if (maxFile && input.length > maxFile) {
            input = input.slice(0, maxFile);
          }
          onChange([...input]);
        } else if (maxFile && dataResult && dataResult.length > maxFile) {
          // eslint-disable-next-line
          onChange(dataResult.slice(0, maxFile));
        } else {
          onChange(dataResult);
        }
      },
    },
  });
  const handleUploadMultiFile = (event: File[]) => {
    if (event) {
      mutate({
        data: {
          files: [...event],
        },
      });
    }
  };
  const handleNameFile = (fileName: string) => {
    const maxLength = 15;
    const extension = fileName.substring(fileName.lastIndexOf(".")); // Lấy đuôi file

    let shortenedFileName = fileName.substring(0, maxLength);

    if (shortenedFileName.length < fileName.length) {
      shortenedFileName += "...";
    }
    return shortenedFileName + extension;
  };

  return (
    <>
      {value && value?.length > 0 ? (
        <Grid
          marginBottom={4}
          sx={{
            border: "1px",
            borderStyle: "solid",
            borderRadius: "12px",
            borderColor: gray[300],
          }}
        >
          {value.map((e, index) => (
            <Box
              key={e?.url}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "7px 14px",
                ...(index < value.length - 1
                  ? {
                      borderBottom: "1px solid #F9FAFB",
                    }
                  : {}),
              }}
            >
              <Box
                component="img"
                sx={{
                  height: 40,
                  width: 40,
                  marginRight: "24px",
                }}
                alt={e?.originalName}
                src={handleShowIcon(e?.extname, e?.url)}
                onError={() => {
                  setIsImage(false);
                }}
              />
              <Box
                sx={{
                  flexGrow: 1,
                }}
              >
                <Typography
                  type="TextSM"
                  fontWeight={500}
                  flex={1}
                  align="left"
                >
                  {handleNameFile(e?.originalName)}
                </Typography>
                <Typography
                  type="TextSM"
                  color={gray[600]}
                  flex={1}
                  align="left"
                >
                  {`${Math.floor(Number(e?.size) / 1024)} KB`}
                </Typography>
                <Box display="flex">
                  <Box display="flex" alignItems="center" paddingRight="8px">
                    <IconButton
                      size="small"
                      onClick={() => {
                        window.open(e?.url);
                      }}
                    >
                      <EyeIcon fontSize="small" color="primary" />
                    </IconButton>
                    <Typography type="TextSM">View</Typography>
                  </Box>
                  {/* <Box display="flex" alignItems="center" paddingRight="8px">
                    <IconButton
                      size="small"
                      onClick={() => {
                        // console.log(value, "value");
                        // handleDownload(value?.url);
                      }}
                    >
                      <DownloadCloud01Icon fontSize="small" color="primary" />
                    </IconButton>
                    <Typography type="TextSM">Download</Typography>
                  </Box> */}
                  {!isViewForm ? (
                    <Box display="flex" alignItems="center" paddingRight="8px">
                      <IconButton
                        size="small"
                        disabled={disable}
                        onClick={() => {
                          const result = value.filter(
                            (file) => file?.url !== e?.url
                          );
                          onChange(result);
                        }}
                      >
                        <Trash03Icon
                          fontSize="small"
                          color={disable ? "disabled" : "primary"}
                        />
                      </IconButton>
                      <Typography
                        type="TextSM"
                        color={disable ? `${gray[300]}` : ""}
                      >
                        Remove
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              </Box>
            </Box>
          ))}
        </Grid>
      ) : null}
      {!isViewForm &&
      (!maxFile || !value || (value && value.length < maxFile)) ? (
        <Button
          component="label"
          variant="text"
          sx={{
            width: "100%",
            paddingX: "0px",
            paddingY: "0px",
            paddingBottom: "6px",
            height: "130px",
          }}
          disabled={disable}
        >
          <Box
            display="inline-block"
            borderRadius="12px"
            border={`1px solid ${primary[300]}`}
            textAlign="center"
            sx={{
              background: base.White,
              cursor: "pointer",
              "&:hover": {
                opacity: 0.7,
              },
              position: "absolute",
              width: "100%",
            }}
            height="130px"
          >
            <FileUploader
              handleChange={(file: File[]) => {
                handleUploadMultiFile(file);
              }}
              name="file"
              types={fileType}
              sx={{ width: 300 }}
              multiple
            >
              <>
                <Box
                  display="inline-flex"
                  marginBottom="12px"
                  marginTop="12px"
                  borderRadius="8px"
                  border={`1px solid ${gray[300]}`}
                  boxShadow={shadows.xs}
                  sx={{ background: base.White }}
                  padding="8px"
                >
                  <UploadCloud02Icon
                    sx={{ color: gray[600] }}
                    fontSize="small"
                  />
                </Box>
                <Typography type="TextSM" color={gray[600]} textAlign="center">
                  <span style={{ color: primary[700], fontWeight: 600 }}>
                    Click to upload
                  </span>
                  &nbsp;or drag and drop
                </Typography>
                <Typography type="TextSM" color={gray[600]} textAlign="center">
                  .doc or PDF(file size maximum 10MB)
                </Typography>
              </>
            </FileUploader>
          </Box>
        </Button>
      ) : null}
    </>
  );
};

const UploadFilesHookForm = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  defaultValue,
  rules,
  shouldUnregister,
  fileType,
  setPropsWithFormState,
  isViewForm,
  maxFile,
  ...innerProps
}: any & { fileType: string[] }) => {
  const { control } = useFormContext<TFieldValues>();
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      rules={rules}
      shouldUnregister={shouldUnregister}
      control={control}
      render={(formState) => {
        const {
          field: { disabled, ref, onChange, value, ...innerField },
          formState: { errors },
        } = formState;
        const error = errors[name];
        const errorMsg = error?.message;
        const helperText =
          typeof errorMsg === "string" ? errorMsg : innerProps?.helperText;
        return (
          <FormControlLabel
            control={
              <Control
                onChange={onChange}
                value={value}
                disable={innerProps.disabled}
                fileType={fileType}
                isViewForm={isViewForm}
                maxFile={maxFile}
              />
            }
            error
            helperText={helperText}
            label={innerProps?.label}
            required={innerProps.required}
          />
        );
      }}
    />
  );
};

export default UploadFilesHookForm;
